/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RaiseVehicleTickets.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { componentStyle } from "../../../config/appTheme";
import RaiseTicketTemplate from "./RaiseTicketTemplate";
import SelectTemplate from "./SelectDialog/SelectTemplate";
import { useDispatch, useSelector } from "react-redux";
import DialogIndex from "./SelectDialog/DialogIndex";
import KeyValueDisplayCard from "./SelectDialog/KeyValueDisplayCard";
import CustomCheckBoxWithoutFormControl from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";
import RaiseTicketRightTemplate from "./RaiseTicketRightTemplate";
import { CAPABILITY_CODE } from "../../../config/appConstants";
import VehicleSavedLinkageCard from "./VehicleSavedLinkageCard";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";
import {
  HandleApiActions,
  labCar,
  legalRequirements,
  userManagementApis,
} from "../../../redux/actions";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import { ENTITY } from "../../../components/customComponentsV2/SmartMatchingDialogContainer/constants/entityTypes";
import AISuggestionDialogContainer from "../../../components/customComponentsV2/SmartMatchingDialogContainer/AiSuggestionDialogContainer";
import testCasesActions from "../../../redux/actions/testCasesActions";
import TestCaseCampaignLinkDialog from "./TestCaseCampaignLinkDialog";

const VEHICLE_COMPONENT_ENTITY_TYPE = "VC";
const VEHICLE_SYSTEM_ENTITY_TYPE = "VS";

function RaiseVehicleTickets({
  savedLinkages,
  setSavedLinkages,
  entityType,
  vehicleTicketMode = "component",
}) {
  const dispatch = useDispatch();
  const isVehicleSystemMode = vehicleTicketMode === "system";
  const vehicleEntityType = isVehicleSystemMode
    ? VEHICLE_SYSTEM_ENTITY_TYPE
    : VEHICLE_COMPONENT_ENTITY_TYPE;
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [zoneList, setZoneList] = useState([]);
  const [isZonalProject, setIsZonalProject] = useState(false);

  //   const [selectedSWMMain, setSelectedSWMMain] = useState(null);
  const [selectedVSMain, setSelectedVSMain] = useState([]);
  const [selectedVSForDisplay, setSelectedVSForDisplay] = useState([]);
  const [selectedVCMain, setSelectedVCMain] = useState([]);
  const [openEESelect, setOpenEESelect] = useState(false);

  const [selectedLegalMain, setSelectedLegalMain] = useState(null);
  const [openLegalSelect, setOpenLegalSelect] = useState(false);
  const [disabledVSComponents, setDisabledVSComponents] = useState([]);

  const [openTestCaseSelect, setOpenTestCaseSelect] = useState(false);
  const [selectedTestCaseForDisplay, setSelectedTestCaseForDisplay] =
    useState(null);
  const [selectedTextCaseMain, setSelectedTestCaseMain] = useState(null);
  const [selectedComponentRequestor, setSelectedComponentRequestor] = useState(
    [],
  );
  const [selectedLegalTechUserDetails, setSelectedLegalTechUserDetails] =
    useState([]);

  const [selectedComplianceRequestors, setSelectedComplianceRequestor] =
    useState([]);
  const [selectedComplianceReviewers, setSelectedComplianceReviewers] =
    useState([]);
  const [selectedComplianceSupplier, setSelectedComplianceSupplier] = useState(
    [],
  );
  const [description, setDescription] = useState("");
  const [
    selectedComponentSupplierDetails,
    setSelectedComponentSupplierDetails,
  ] = useState([]);
  const [selectedDynamicForm, setSelectedDynamicForm] = useState(null);

  const [dynamicFormFilteredData, setDynamicFormFilteredData] = useState(null);
  const [showDynamicSelect, setShowDynamicSelect] = useState(false);
  const [disabledLegalRequirement, setDisabledLegalRequirement] = useState([]);
  const [allUsersList, setAllUserList] = useState([]);
  const [openAISuggestion, setOpenAISuggestion] = useState(false);
  const [showSmartMatchDialog, setshowSmartMatchDialog] = useState(false);
  const [componentToBeMatched, setComponentToBeMatched] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [
    selectedSmartMatchTestCaseSuggestions,
    setSelectedSmartMatchTestCaseSuggestions,
  ] = useState([]);
  const [disableSmartMatch, setDisableSmartMatch] = useState(false);
  const [closeAiDialogOnAdd, setCloseAiDialogOnAdd] = useState(false);
  const [openCampaignLinkDialog, setOpenCampaignLinkDialog] = useState(false);
  const [testCasesToCampaignLink, setTestCasesToCampaignLink] = useState([]);
  const [formResetKey, setFormResetKey] = useState(0);
  const savedLinkagesRef = React.useRef(savedLinkages);
  useEffect(() => {
    savedLinkagesRef.current = savedLinkages;
  }, [savedLinkages]);
  const getPrimarySelectedVC = () => selectedVCMain?.[0] || null;
  const getPrimarySelectedVehicleEntity = () =>
    isVehicleSystemMode ? selectedVSMain?.[0] || null : getPrimarySelectedVC();
  const getVehicleSystemId = (vehicleSystem) =>
    Number(vehicleSystem?.id || vehicleSystem?.vs_id);
  const getVehicleSystemLinkedLegalIds = (vehicleSystem) =>
    new Set(
      (vehicleSystem?.legal_req_ids || [])
        .map((lrId) => Number(lrId))
        .filter((lrId) => Number.isFinite(lrId)),
    );
  const isVehicleSystemLinkedWithLegalRequirement = (
    vehicleSystem,
    lrId = selectedLegalMain?.lr_id,
  ) => {
    const legalRequirementId = Number(lrId);
    if (!Number.isFinite(legalRequirementId)) return false;

    const vehicleSystemId = getVehicleSystemId(vehicleSystem);
    return (
      getVehicleSystemLinkedLegalIds(vehicleSystem).has(legalRequirementId) ||
      disabledVSComponents.includes(vehicleSystemId)
    );
  };
  const getSelectedVehicleSystemLinkedCount = (lrId) => {
    const legalRequirementId = Number(lrId);
    if (!isVehicleSystemMode || !Number.isFinite(legalRequirementId)) return 0;

    return selectedVSMain.filter((vehicleSystem) =>
      getVehicleSystemLinkedLegalIds(vehicleSystem).has(legalRequirementId),
    ).length;
  };
  const shouldShowLegalRequirementLinkedInfo = (lrId) => {
    const linkedCount = getSelectedVehicleSystemLinkedCount(lrId);
    return linkedCount > 0 && linkedCount < selectedVSMain.length;
  };

  const commitSelectedTestCases = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) {
      return;
    }

    setSelectedTestCaseMain((prev) => {
      const currentSelection = Array.isArray(prev) ? prev : [];
      const mergedSelection = [...currentSelection];

      testCases.forEach((testCase) => {
        const alreadySelected = mergedSelection.some(
          (selectedItem) =>
            selectedItem?.test_case_id === testCase?.test_case_id,
        );
        if (!alreadySelected) {
          mergedSelection.push({ ...testCase, is_campaign_linked: true });
        }
      });

      setSelectedTestCaseForDisplay(mergedSelection);
      return mergedSelection;
    });
  };

  const clearCampaignLinkFields = () => {
    setOpenCampaignLinkDialog(false);
    setTestCasesToCampaignLink([]);
  };

  const openCampaignDialogForTestCases = (testCases = []) => {
    setTestCasesToCampaignLink(testCases);
    setOpenCampaignLinkDialog(true);
  };

  const handleSelectedTestCases = (testCases = []) => {
    const currentSelectedIds = new Set(
      (selectedTextCaseMain || []).map((tc) => tc?.test_case_id),
    );
    const testCasesReadyForTicket = testCases.filter(
      (tc) =>
        tc?.is_campaign_linked !== false ||
        currentSelectedIds.has(tc?.test_case_id),
    );
    const unlinkedTestCases = testCases.filter(
      (tc) =>
        tc?.is_campaign_linked === false &&
        !currentSelectedIds.has(tc?.test_case_id),
    );

    setSelectedTestCaseMain(testCasesReadyForTicket);
    setSelectedTestCaseForDisplay(testCasesReadyForTicket);

    if (unlinkedTestCases.length > 0) {
      openCampaignDialogForTestCases(unlinkedTestCases);
    }
  };

  // ── Test Case dialog entity context ───────────────────────────────────────
  // VS mode  -> entity_type "vs" + the selected Vehicle System's own id
  // VC mode  -> entity_type "vc" + the selected Vehicle Component's id
  const testCaseEntityType = isVehicleSystemMode ? "vs" : "vc";
  const testCaseEntityId = isVehicleSystemMode
    ? selectedVSMain[0]?.id
    : selectedVSMain[0]?.vcData?.[0]?.id;

  const getRecommendationVS = (recommendation, fallbackVC = null) => {
    const parentContext =
      recommendation?.parentContext ||
      recommendation?._ai?.targetContext?.parentContext;
    const parent =
      recommendation?.parent || recommendation?._ai?.targetContext?.parent;
    const vsId =
      fallbackVC?.pr_vs_id ||
      recommendation?.pr_vs_id ||
      parentContext?.parent_id ||
      parent?.entityId;

    return {
      id: vsId,
      name:
        parentContext?.parent_name ||
        parentContext?.name ||
        parent?.description ||
        parent?.number ||
        "Vehicle System",
      description: parentContext?.description || parent?.description || "",
    };
  };

  const syncSelectedVehicleComponent = (vcDetails, recommendation = null) => {
    if (!vcDetails?.id) return;

    const vsDetails = getRecommendationVS(recommendation, vcDetails);
    if (!vsDetails?.id) return;

    const addVCToVS = (prev = []) => {
      const existingVS = prev.find(
        (vs) => Number(vs.id) === Number(vsDetails.id),
      );

      if (existingVS) {
        const existingVCData = Array.isArray(existingVS.vcData)
          ? existingVS.vcData
          : [];
        if (
          existingVCData.some((vc) => Number(vc.id) === Number(vcDetails.id))
        ) {
          return prev;
        }

        return prev.map((vs) =>
          Number(vs.id) === Number(vsDetails.id)
            ? {
              ...vs,
              vcData: [...existingVCData, vcDetails],
            }
            : vs,
        );
      }

      return [
        ...prev,
        {
          ...vsDetails,
          vcData: [vcDetails],
        },
      ];
    };

    setSelectedVSMain(addVCToVS);
    setSelectedVSForDisplay(addVCToVS);
  };

  const handleSmartMatchRequest = async (componentDetails, tag) => {
    setDisableSmartMatch(true);
    setCloseAiDialogOnAdd(
      tag === vehicleEntityType || tag === ENTITY.TYPE.LEGAL,
    );

    const primaryVehicleEntity = getPrimarySelectedVehicleEntity();
    const componentName =
      tag === vehicleEntityType
        ? componentDetails?.description
        : primaryVehicleEntity?.description || primaryVehicleEntity?.name;

    if (componentName?.length > 0) {
      setComponentToBeMatched([componentName]);
    } else {
      setComponentToBeMatched(["smart-match"]);
    }

    const sourceType =
      tag === ENTITY.TYPE.LEGAL ? vehicleEntityType : ENTITY.TYPE.LEGAL;

    const sourceId =
      tag === ENTITY.TYPE.LEGAL
        ? componentDetails?.id ||
        componentDetails?.vs_id ||
        componentDetails?.vc_id
        : componentDetails?.lr_id;

    const selectedVehicleScopes = (
      (isVehicleSystemMode ? selectedVSMain : selectedVCMain) || []
    )
      .map((vehicleEntity) => ({
        entityType: vehicleEntityType,
        entityId: Number(
          vehicleEntity?.id || vehicleEntity?.vs_id || vehicleEntity?.vc_id,
        ),
      }))
      .filter((scopeEntity) => Number.isFinite(scopeEntity.entityId));

    const shouldUseVehicleScope = tag === ENTITY.TYPE.TEST_CASE;
    const source =
      sourceType === vehicleEntityType
        ? selectedVehicleScopes
        : [{ entityType: sourceType, entityId: sourceId }];

    setshowSmartMatchDialog(true);
    setOpenAISuggestion(true);
    setSelectedEntity({
      source,
      targetEntityTypes: [tag],
      topK: 20,
      minConfidence: 0.1,
      description: componentName,
      scope: shouldUseVehicleScope
        ? {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
          scopeEntities: selectedVehicleScopes,
        }
        : {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
        },
    });
    setDisableSmartMatch(false);
  };

  const handleLinkSuggestions = (recommendation) => {
    if (recommendation.type === vehicleEntityType) {
      if (isVehicleSystemMode) {
        const vehicleSystem = {
          ...recommendation,
          id: recommendation?.id || recommendation?.vs_id,
        };
        setSelectedVSMain([vehicleSystem]);
        setSelectedVSForDisplay([vehicleSystem]);
      } else {
        handleVehicleComponentAddClick(recommendation);
      }
      setOpenAISuggestion(false);
    } else if (recommendation.type === "LEGAL_REQUIREMENT") {
      handleLegalAddClick(recommendation);
      setOpenAISuggestion(false);
    } else if (recommendation.type === "TEST_CASE") {
      handleTestCaseAddClick(recommendation);
    }
  };

  const handleVehicleComponentAddClick = (recommendation) => {
    fetchVehicleComponentRecommendationContext(recommendation);
  };

  const fetchVehicleComponentRecommendationContext = async (recommendation) => {
    const vcId = recommendation?.id || recommendation?.vc_id;
    if (!vcId) return;

    const resp = await dispatch(
      HandleApiActions({
        ...labCar.get_vehicle_components,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedVariantDetails?.variant_id),
          vc_id: Number(vcId),
          id: Number(vcId),
          page_number: 1,
          page_size: 1,
        },
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    const vcData = !resp.error && resp.data?.length ? resp.data[0] : null;
    const fallbackVC = {
      ...recommendation,
      id: vcId,
      pr_vs_id:
        recommendation?.pr_vs_id ||
        recommendation?.parentContext?.parent_id ||
        recommendation?.parent?.entityId,
    };

    syncSelectedVehicleComponent(vcData || fallbackVC, recommendation);
  };

  const handleLegalAddClick = (recommendation) => {
    fetchLegalRecommendationContext({ lr_id: recommendation.lr_id });
  };

  const fetchLegalRecommendationContext = async (params) => {
    const resp = await dispatch(
      HandleApiActions({
        https_method: "GET",
        ...legalRequirements.get_legal_requirements_details_new,
        params: params,
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    if (!resp.error && resp.data && resp.data.length > 0) {
      const legalRequirementData = resp.data[0];
      setSelectedLegalMain(legalRequirementData);
      setSelectedLegalTechUserDetails(legalRequirementData.tech_service_users);
    }
  };

  const handleTestCaseAddClick = (recommendation) => {
    fetchTestCaseRecommendationContext({
      manual_test_case_id: recommendation.manual_test_case_id,
    });
  };

  const fetchTestCaseRecommendationContext = async (params) => {
    const resp = await dispatch(
      HandleApiActions({
        https_method: "GET",
        ...testCasesActions.get_test_cases,
        params: params,
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    if (!resp.error && resp.data && resp.data.length > 0) {
      const testCaseData = resp.data[0];
      if (testCaseData?.is_campaign_linked === false) {
        openCampaignDialogForTestCases([testCaseData]);
      } else {
        setSelectedSmartMatchTestCaseSuggestions((prev) => {
          if (
            prev.some((tc) => tc.test_case_id === testCaseData.test_case_id)
          ) {
            return prev;
          }
          return [...prev, testCaseData];
        });
      }
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      let resp = await dispatch(
        HandleApiActions({
          ...userManagementApis.get_users_list_pa,
          show_toast: false,
          params: { page_number: 1, page_size: 500 },
        }),
      );
      setAllUserList(resp.data);
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    setZoneList(selectedProjectDetails?.project_zones);
    setIsZonalProject(selectedProjectDetails?.is_zonal);
  }, [selectedProjectDetails]);

  useEffect(() => {
    clearForm();
    setSavedLinkages([]);
  }, [selectedProjectDetails, selectedVariantDetails]);

  const clearForm = () => {
    setFormResetKey((k) => k + 1);
    setSelectedVSMain([]);
    setSelectedVSForDisplay([]);
    setSelectedVCMain([]);
    setSelectedLegalMain(null);
    setSelectedDynamicForm(null);
    setSelectedComplianceRequestor([]);
    setSelectedComplianceReviewers([]);
    setDescription("");
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedComponentSupplierDetails([]);
    setSelectedComplianceSupplier([]);
    setDisabledVSComponents([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
    clearCampaignLinkFields();
  };

  useEffect(() => {
    let linkedLRIds = [];
    const linkedLRCounts = new Map();
    const vcList = [];
    for (let vs of selectedVSMain) {
      // In VS mode: collect legal_req_ids directly from the VS
      if (isVehicleSystemMode) {
        const uniqueLegalIdsForVS = getVehicleSystemLinkedLegalIds(vs);
        for (let lr of uniqueLegalIdsForVS) {
          linkedLRCounts.set(lr, (linkedLRCounts.get(lr) || 0) + 1);
        }
      }

      // In VC mode: collect legal_req_ids from each VC under the VS
      const vehicleComponents = Array.isArray(vs?.vcData) ? vs.vcData : [];
      for (let vc of vehicleComponents) {
        vcList.push(vc);
        if (!isVehicleSystemMode) {
          for (let lr of vc.legal_req_ids || []) {
            linkedLRIds.push(Number(lr));
          }
        }
      }
    }
    if (isVehicleSystemMode) {
      linkedLRIds = Array.from(linkedLRCounts.entries())
        .filter(([, linkedCount]) => linkedCount === selectedVSMain.length)
        .map(([lrId]) => lrId);
    }
    setSelectedVCMain(vcList);
    setDisabledLegalRequirement(linkedLRIds);
  }, [selectedVSMain, isVehicleSystemMode]);

  useEffect(() => {
    if (selectedSmartMatchTestCaseSuggestions.length > 0) {
      setSelectedTestCaseMain(selectedSmartMatchTestCaseSuggestions);
      setSelectedTestCaseForDisplay(selectedSmartMatchTestCaseSuggestions);
    }
  }, [selectedSmartMatchTestCaseSuggestions]);

  useEffect(() => {
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
  }, [selectedVSMain, selectedLegalMain]);

  const getDisabledVSListForLR = async ({ lrId }) => {
    if (!lrId) {
      setDisabledVSComponents([]);
      return;
    }

    if (
      !selectedProjectDetails?.project_id ||
      !selectedVariantDetails?.variant_id
    ) {
      setDisabledVSComponents([]);
      return;
    }

    const resp = await dispatch(
      HandleApiActions({
        ...softwareIntersectionAPI.get_vs_for_legal_requirements,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedVariantDetails?.variant_id),
          lr_id: Number(lrId),
        },
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    if (resp.error) {
      setDisabledVSComponents([]);
      return;
    }

    const vsIds = resp?.data?.[0]?.vs_ids || [];
    const normalizedIds = vsIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    setDisabledVSComponents(normalizedIds);
  };

  useEffect(() => {
    if (selectedLegalMain?.lr_id) {
      getDisabledVSListForLR({ lrId: selectedLegalMain.lr_id });
    } else {
      setDisabledVSComponents([]);
    }
  }, [selectedLegalMain, selectedProjectDetails, selectedVariantDetails]);

  // sonar handler
  const handleVSMainChange = (checked, item) => {
    if (checked) {
      setSelectedVSMain((prev) => [...prev, item]);
      setSelectedVSForDisplay((prev) => [...prev, item]);
    } else {
      setSelectedVSMain((prev) => prev.filter((vs) => vs.id !== item.id));
      setSelectedVSForDisplay((prev) => prev.filter((vs) => vs.id !== item.id));
    }
  };

  const getUserWithCapability = (u, allUsersList) => {
    for (let au of allUsersList) {
      if (au.user_id == u.user_id) {
        return {
          ...u,
          capability_code: au.role_details.capability_code,
        };
      }
    }
    return undefined;
  };

  const insertCapabilityCodeToUserList = (userDetails, allUsersList) => {
    if (!userDetails || !Array.isArray(userDetails)) return [];
    const capabilityCodeAddedUser = userDetails.map((u) =>
      getUserWithCapability(u, allUsersList),
    );

    return capabilityCodeAddedUser;
  };

  const removeDuplicates = (arr = []) => {
    if (!Array.isArray(arr)) return [];

    const seen = new Set();

    return arr.filter((user) => {
      if (!user?.user_id || seen.has(user.user_id)) {
        return false;
      }
      seen.add(user.user_id);
      return true;
    });
  };

  const getRequestor = ({
    owner = [],
    coOwner = [],
    editor = [],
    viewer = [],
  }) => {
    const uniqueOwners = removeDuplicates(
      insertCapabilityCodeToUserList(owner, allUsersList),
    );

    const uniqueCoOwners = removeDuplicates(
      insertCapabilityCodeToUserList(coOwner, allUsersList),
    );

    const uniqueEditors = removeDuplicates(
      insertCapabilityCodeToUserList(editor, allUsersList),
    );

    const uniqueViewers = removeDuplicates(
      insertCapabilityCodeToUserList(viewer, allUsersList),
    );

    const selectedComponentRequestor = [...uniqueOwners, ...uniqueCoOwners];

    const selectedComponentSupplierDetails = [
      ...uniqueCoOwners,
      ...uniqueEditors,
      ...uniqueViewers,
    ].filter((ele) => ele?.capability_code === CAPABILITY_CODE.supplier);

    return {
      requestor: selectedComponentRequestor,
      supplier: selectedComponentSupplierDetails,
    };
  };

  const getUserIds = (users = []) =>
    Array.isArray(users) ? users.map((u) => u?.user_id).filter(Boolean) : [];

  const getVcToLegal = (vcData = []) =>
    vcData
      .map((vc) => {
        if (!vc) return null;

        const { requestor = [], supplier = [] } = getRequestor({
          owner: Array.isArray(vc?.owner_details) ? vc.owner_details : [],
          coOwner: Array.isArray(vc?.co_owner_details)
            ? vc.co_owner_details
            : [],
          editor: Array.isArray(vc?.edit_user_details)
            ? vc.edit_user_details
            : [],
          viewer: Array.isArray(vc?.view_user_details)
            ? vc.view_user_details
            : [],
        });

        return {
          vc_id: vc?.id,
          oem_users: getUserIds(requestor),
          suppliers: getUserIds(supplier),
        };
      })
      .filter(Boolean);

  const getTestCaseIds = (testCases = []) =>
    Array.isArray(testCases)
      ? testCases.map((tc) => tc?.test_case_id).filter(Boolean)
      : [];

  return (
    <Box>
      <Grid container>
        <Grid item sm={12} sx={{ borderBottom: "1px solid black" }}>
          <Box sx={{ display: "flex" }}>
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              Select Entities and Fill in the Details
            </Typography>
          </Box>
        </Grid>

        <Grid item sm={12} sx={{ pt: 1 }}>
          <RaiseTicketTemplate
            leftContent={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                <Box>
                  <SelectTemplate
                    heading={
                      isVehicleSystemMode
                        ? "Select Vehicle System"
                        : "Select Vehicle Component"
                    }
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenEESelect(true);
                    }}
                    enableEdit={selectedVSForDisplay.length}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedLegalMain !== null}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        vehicleEntityType,
                      )
                    }
                    content={
                      selectedVSForDisplay.length ? (
                        <Box sx={{ pl: 2 }}>
                          {selectedVSForDisplay.map((item, index) => {
                            const selectedVehicleSystem = item;
                            const selectedVehicleComponents = Array.isArray(
                              selectedVehicleSystem?.vcData,
                            )
                              ? selectedVehicleSystem.vcData
                              : [];
                            const selectedVehicleComponentNames =
                              selectedVehicleComponents
                                .map((vc) => vc?.name)
                                .filter(Boolean)
                                .join(", ");
                            const selectedVehicleComponentNumbers =
                              selectedVehicleComponents
                                .map((vc) => vc?.component_number)
                                .filter(Boolean)
                                .join(", ");
                            const getCheckedValueForVS = () => {
                              for (let vs of selectedVSMain) {
                                if (vs.id === item.id) {
                                  return true;
                                }
                              }
                              return false;
                            };
                            return (
                              <Box
                                key={index}
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "start",
                                  position: "relative",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "10%",
                                  }}
                                >
                                  <CustomCheckBoxWithoutFormControl
                                    value={getCheckedValueForVS()}
                                    onChange={(e) =>
                                      handleVSMainChange(
                                        e.target.checked,
                                        item,
                                        // setSelectedVSMain,
                                      )
                                    }
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    width: "90%",
                                  }}
                                >
                                  <KeyValueDisplayCard
                                    data={[
                                      {
                                        key: "Name",
                                        value: isVehicleSystemMode
                                          ? selectedVehicleSystem?.name || "-"
                                          : selectedVehicleComponentNames ||
                                          selectedVehicleSystem?.name ||
                                          "-",
                                        style: { fontWeight: 600 },
                                      },
                                      {
                                        key: "Description",
                                        value:
                                          selectedVehicleSystem?.description ||
                                          "-",
                                      },
                                      {
                                        key: "Vehicle Components",
                                        value: isVehicleSystemMode
                                          ? selectedVehicleComponentNames || "-"
                                          : selectedVehicleComponentNumbers ||
                                          "-",
                                      },
                                    ].filter(
                                      (field) =>
                                        !isVehicleSystemMode ||
                                        field.key !== "Vehicle Components",
                                    )}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Legal Requirement"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenLegalSelect(true);
                    }}
                    enableEdit={selectedLegalMain !== null}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={
                      getPrimarySelectedVehicleEntity() !== null
                    }
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        getPrimarySelectedVehicleEntity(),
                        ENTITY.TYPE.LEGAL,
                      )
                    }
                    content={
                      <Box sx={{ pl: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "left",
                            width: "100%",
                            pl: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              fontWeight: 600,
                            }}
                          >
                            Legal Standard:
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Name: ${selectedLegalMain
                              ? selectedLegalMain.legal_standard_details?.name
                              : ""
                              }`}
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Number: ${selectedLegalMain
                              ? selectedLegalMain.legal_standard_details
                                ?.number
                              : ""
                              }`}
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Revision: ${selectedLegalMain
                              ? selectedLegalMain.legal_standard_details
                                ?.revision
                              : ""
                              }`}
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              fontWeight: 600,
                            }}
                          >
                            Legal Requirement:
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Number: ${selectedLegalMain
                              ? selectedLegalMain.req_number
                              : ""
                              }`}
                          </Typography>

                          <CustomToolTip
                            length={200}
                            style={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Description: ${selectedLegalMain
                              ? selectedLegalMain.description
                              : ""
                              }`}
                          </CustomToolTip>
                        </Box>
                      </Box>
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Test Case"}
                    onClickAdd={() => {
                      setOpenTestCaseSelect(true);
                    }}
                    addButtonDisabled={
                      selectedLegalMain === null || selectedVSMain.length === 0
                    }
                    disableSmartMatch={disableSmartMatch}
                    enableEdit={
                      selectedTestCaseForDisplay !== null &&
                      selectedTestCaseForDisplay.length > 0
                    }
                    showSmartMatch={
                      getPrimarySelectedVehicleEntity() !== null &&
                      selectedLegalMain !== null
                    }
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        ENTITY.TYPE.TEST_CASE,
                      )
                    }
                    content={
                      <Box sx={{ pl: 2 }}>
                        {selectedTestCaseForDisplay &&
                          selectedTestCaseForDisplay.length &&
                          selectedTestCaseForDisplay.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  width: "10%",
                                }}
                              >
                                <CustomCheckBoxWithoutFormControl
                                  value={
                                    selectedTextCaseMain?.some(
                                      (selectedItem) =>
                                        selectedItem?.test_case_id ===
                                        item?.test_case_id,
                                    ) || false
                                  }
                                  onChange={(e) => {
                                    const currentSelection =
                                      selectedTextCaseMain || [];
                                    if (
                                      e.target.checked &&
                                      item?.is_campaign_linked === false
                                    ) {
                                      openCampaignDialogForTestCases([item]);
                                      return;
                                    }
                                    const newSelectedTestCase = e.target.checked
                                      ? currentSelection.some(
                                        (selectedItem) =>
                                          selectedItem?.test_case_id ===
                                          item?.test_case_id,
                                      )
                                        ? currentSelection
                                        : [...currentSelection, item]
                                      : currentSelection.filter(
                                        (selectedItem) =>
                                          selectedItem?.test_case_id !==
                                          item?.test_case_id,
                                      );
                                    setSelectedTestCaseMain(
                                      newSelectedTestCase,
                                    );
                                    setSelectedTestCaseForDisplay(
                                      newSelectedTestCase,
                                    );
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  width: "60%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    p: 1,
                                  }}
                                >
                                  <Typography
                                    key={index}
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case ID: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.test_case_id}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    key={index}
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case Title: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.name}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    key={index}
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Description: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.description}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  width: "30%",
                                }}
                              >
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    }
                  />
                </Box>
              </Box>
            }
            rightContent={
              <RaiseTicketRightTemplate
                selectedComponentRequestor={selectedComponentRequestor}
                selectedLegalTechUserDetails={selectedLegalTechUserDetails}
                selectedComplianceRequestors={selectedComplianceRequestors}
                setSelectedComplianceRequestor={setSelectedComplianceRequestor}
                selectedComplianceReviewers={selectedComplianceReviewers}
                formType={isVehicleSystemMode ? "vehicle-system" : "vehicle-component"}
                setSelectedComplianceReviewers={setSelectedComplianceReviewers}
                description={description}
                setDescription={setDescription}
                selectedComponentSupplierDetails={
                  selectedComponentSupplierDetails
                }
                selectedComplianceSupplier={selectedComplianceSupplier}
                setSelectedComplianceSupplier={setSelectedComplianceSupplier}
                selectedDynamicForm={selectedDynamicForm}
                setSelectedDynamicForm={setSelectedDynamicForm}
                dynamicFormFilteredData={dynamicFormFilteredData}
                setDynamicFormFilteredData={setDynamicFormFilteredData}
                showDynamicSelect={showDynamicSelect}
                setShowDynamicSelect={setShowDynamicSelect}
                localSubmitDisabled={
                  !selectedVSMain.length ||
                  (!isVehicleSystemMode &&
                    selectedVSMain.some((item) => !item.vcData?.length)) ||
                  selectedLegalMain === null ||
                  description === "" ||
                  description.trim().length < 3 ||
                  selectedComplianceRequestors.length === 0 ||
                  selectedComplianceReviewers.length === 0
                }
                handleLocalSaveForm={() => {
                  try {
                    if (
                      !Array.isArray(selectedVSMain) ||
                      selectedVSMain.length === 0
                    ) {
                      return;
                    }

                    const vehicleSystemsForPayload = isVehicleSystemMode
                      ? selectedVSMain.filter(
                        (item) =>
                          !isVehicleSystemLinkedWithLegalRequirement(item),
                      )
                      : selectedVSMain;

                    if (vehicleSystemsForPayload.length === 0) {
                      return;
                    }

                    const linkedVSdetails = vehicleSystemsForPayload
                      .map((item) => {
                        if (!item) return null;

                        const vcData = Array.isArray(item?.vcData)
                          ? item.vcData
                          : [];

                        const vcNames = Array.isArray(item?.vcData)
                          ? item.vcData.map((vc) => vc?.name).filter(Boolean).join(", ")
                          : "";
                        const basePayload = {
                          vs_id: item?.id,
                          vs_name: isVehicleSystemMode
                            ? item?.name || item?.description || "-"
                            : vcNames || item?.name || "-",
                          lr_id: selectedLegalMain?.lr_id,
                          test_case_ids: getTestCaseIds(selectedTextCaseMain),
                          test_cases: selectedTextCaseMain || [],
                          oem_users: Array.isArray(selectedComplianceRequestors)
                            ? selectedComplianceRequestors
                              .map((user) => user?.value)
                              .filter(Boolean)
                            : [],
                          suppliers: selectedComplianceSupplier.map(
                            (user) => user.value,
                          ),
                          tech_service_users: Array.isArray(
                            selectedComplianceReviewers,
                          )
                            ? selectedComplianceReviewers
                              .map((user) => user?.value)
                              .filter(Boolean)
                            : [],
                          description: description || "",
                          ...(showDynamicSelect
                            ? { dynamic_form: dynamicFormFilteredData }
                            : {}),
                        };

                        if (isVehicleSystemMode) {
                          return basePayload;
                        }

                        return {
                          ...basePayload,
                          vc_to_legal: getVcToLegal(vcData),
                        };
                      })
                      .filter(Boolean);

                    setSavedLinkages([...savedLinkagesRef.current, ...linkedVSdetails]);
                    clearForm();
                  } catch (error) {
                    alert(
                      "An error occurred while saving. Please check the console for details.",
                    );
                  }
                }}
                savedLinkageCard={
                  <VehicleSavedLinkageCard
                    savedLinkages={savedLinkages}
                    setSavedLinkages={setSavedLinkages}
                    entityType={"vs"}
                    onRemove={clearForm}
                  />
                }
              />
            }
          />
        </Grid>
      </Grid>

      <DialogIndex
        key={formResetKey}
        openEESelect={openEESelect}
        setOpenEESelect={setOpenEESelect}
        isZonalProject={isZonalProject}
        disabledLegalRequirement={disabledLegalRequirement}
        shouldShowLegalRequirementLinkedInfo={
          shouldShowLegalRequirementLinkedInfo
        }
        setDisabledLegalRequirement={setDisabledLegalRequirement}
        zoneList={zoneList}
        savedLinkages={savedLinkages}
        disabledVSComponents={disabledVSComponents}
        disableLrRadioButton={(item) => {
          const eeExists = savedLinkages?.find(
            (linkage) => linkage.id === selectedVCMain?.id,
          );
          return eeExists
            ? eeExists.legal?.some((legal) => legal.lr_id === item.lr_id)
            : false;
        }}
        selectedVSMain={selectedVSMain}
        requireVehicleComponentSelection={!isVehicleSystemMode}
        vehicleDialogTitle={
          isVehicleSystemMode
            ? "Select Vehicle System"
            : "Select Vehicle Component"
        }
        onClickAdd={(data) => {
          setSelectedVSMain(data);
          setSelectedVSForDisplay(data);
          setOpenEESelect(false);

          let allOwners = [];
          let allCoOwners = [];
          let allEditors = [];
          let allViewers = [];

          data.forEach((item) => {
            allOwners.push(...(item.owner_details ?? []));
            allCoOwners.push(...(item.co_owners ?? []));
            allEditors.push(...(item.edit_user_details ?? []));
            allViewers.push(...(item.view_user_details ?? []));
          });

          const removeDuplicates = (arr) => {
            const seen = new Set();
            return arr.filter((user) => {
              if (seen.has(user.user_id)) {
                return false;
              }
              seen.add(user.user_id);
              return true;
            });
          };
          const uniqueOwners = removeDuplicates(allOwners);
          const uniqueCoOwners = removeDuplicates(allCoOwners);
          const uniqueEditors = removeDuplicates(allEditors);
          const uniqueViewers = removeDuplicates(allViewers);
          let selectedComponentRequestorTemp = [
            ...uniqueOwners,
            ...uniqueCoOwners,
          ].filter((ele) => {
            return ele.capability_code !== CAPABILITY_CODE.supplier;
          });
          setSelectedComponentRequestor(selectedComponentRequestorTemp);

          let selectedComponentSupplierDetails = [
            ...uniqueCoOwners,
            ...uniqueEditors,
            ...uniqueViewers,
          ];
          setSelectedComponentSupplierDetails(
            selectedComponentSupplierDetails.filter((ele) => {
              return ele.capability_code === CAPABILITY_CODE.supplier;
            }),
          );
        }}
        openLegalSelect={openLegalSelect}
        setOpenLegalSelect={setOpenLegalSelect}
        selectedLegalMain={selectedLegalMain}
        setSelectedLegalMain={setSelectedLegalMain}
        onClickAddLegal={(data) => {
          setSelectedLegalMain(data);
          setOpenLegalSelect(false);
          setSelectedLegalTechUserDetails(data.tech_service_users);
        }}
        openTestCaseSelect={openTestCaseSelect}
        setOpenTestCaseSelect={setOpenTestCaseSelect}
        selectedTextCaseMain={selectedTextCaseMain}
        onClickAddTestCase={(data) => {
          handleSelectedTestCases(data);
          setOpenTestCaseSelect(false);
        }}
        entityType={"vs"}
        entityID={selectedVSMain[0]?.vcData?.[0]?.id}
        testCaseEntityType={testCaseEntityType}
        testCaseEntityId={testCaseEntityId}
      />
      {showSmartMatchDialog && componentToBeMatched?.length > 0 && (
        <AISuggestionDialogContainer
          open={openAISuggestion}
          onClose={() => setOpenAISuggestion(false)}
          entity={selectedEntity}
          onLinkRequest={handleLinkSuggestions}
          onUnlinkRequest={() => { }}
          closeDialogOnAdd={closeAiDialogOnAdd}
        />
      )}
      <TestCaseCampaignLinkDialog
        open={openCampaignLinkDialog}
        testCasesToLink={testCasesToCampaignLink}
        projectId={selectedProjectDetails?.project_id}
        variantId={selectedVariantDetails?.variant_id}
        onClose={clearCampaignLinkFields}
        onSuccess={commitSelectedTestCases}
      />
    </Box>
  );
}

export default RaiseVehicleTickets;
